import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';

// Relógios do BPO que estão CORRENDO para o usuário logado (tempo-no-cliente e
// cronômetro de tarefa) — alimentam o widget flutuante (TempoFlutuante) que
// acompanha o operador em qualquer página. A fonte de verdade é o servidor
// (/api/bpo/tempo-ativo/): o navegador só soma o tempo desde a última resposta,
// relógio do servidor e do navegador nunca são comparados.

const TempoAtivoContext = createContext({
  relogios: [],
  buscadoEm: 0,
  sincronizar: () => Promise.resolve(),
  pararRelogio: () => Promise.resolve(),
});

// ─── retomada pós-F5 ───
// O pagehide para o relógio do cliente (fechar a aba tem que parar). Para o F5
// não "desligar" a empresa, guardamos o que estava rodando; se o app voltar em
// até 2 minutos, o provider religa sozinho — o acumulado fica no banco.
const LS_CHAVE = 'bpoTempoClienteAtivo';
const RETOMADA_MS = 2 * 60 * 1000;

export const guardarTempoAtivoLS = (itens) => {
  try { localStorage.setItem(LS_CHAVE, JSON.stringify({ itens, ts: Date.now() })); } catch { /* sem storage, sem retomada */ }
};
export const limparTempoAtivoLS = () => {
  try { localStorage.removeItem(LS_CHAVE); } catch { /* nada a limpar */ }
};
const lerTempoAtivoLS = () => {
  try {
    const bruto = localStorage.getItem(LS_CHAVE);
    if (!bruto) return null;
    const dado = JSON.parse(bruto);
    return Array.isArray(dado.itens) && dado.ts ? dado : null;
  } catch { return null; }
};

const API_BASE = process.env.REACT_APP_API_BASE;
const headers = () => ({ Authorization: `Bearer ${localStorage.getItem('access')}` });

// O relógio é recurso de OPERADOR INTERNO (id_bpo = 0 no JWT). Usuário cliente
// (id_bpo > 0) não tem acesso — sem este guard o polling toma 403 em loop.
const ehOperadorInterno = () => {
  try {
    const token = localStorage.getItem('access');
    if (!token) return false;
    const payload = JSON.parse(atob(token.split('.')[1]));
    return payload.id_bpo === 0;
  } catch {
    return false;
  }
};

export const TempoAtivoProvider = ({ children }) => {
  const [relogios, setRelogios] = useState([]);
  const [buscadoEm, setBuscadoEm] = useState(0);
  const relogiosRef = useRef([]);          // para o pagehide (sem depender de closure)
  const location = useLocation();

  const sincronizar = useCallback(() => {
    if (!localStorage.getItem('access') || !ehOperadorInterno()) {
      relogiosRef.current = [];
      setRelogios([]);
      return Promise.resolve();
    }
    return axios
      .get(`${API_BASE}/api/bpo/tempo-ativo/`, { headers: headers() })
      .then((res) => {
        const lista = res.data.relogios || [];
        relogiosRef.current = lista;
        setRelogios(lista);
        setBuscadoEm(Date.now());
      })
      .catch(() => { /* erro transitório: mantém o estado e o polling tenta de novo */ });
  }, []);

  const pararRelogio = useCallback((r) => {
    if (r.tipo === 'tarefa') {
      // mesma semântica da página: parar a tarefa devolve o relógio ao cliente
      return axios
        .patch(`${API_BASE}/api/bpo/tarefas/${r.tarefa_id}/`, { parar: true }, { headers: headers() })
        .then(() => axios.post(`${API_BASE}/api/bpo/tempo-cliente/`,
          { area: r.area, empresa: r.empresa, acao: 'iniciar' }, { headers: headers() }))
        .then(() => guardarTempoAtivoLS([{ area: r.area, empresa: r.empresa }]))
        .catch(() => {})
        .finally(sincronizar);
    }
    return axios
      .post(`${API_BASE}/api/bpo/tempo-cliente/`,
        { area: r.area, empresa: '', acao: 'parar' }, { headers: headers() })
      .then(() => limparTempoAtivoLS())
      .catch(() => {})
      .finally(sincronizar);
  }, [sincronizar]);

  // retomada pós-F5: religa as sessões de cliente registradas há menos de 2 min
  useEffect(() => {
    const guardado = lerTempoAtivoLS();
    if (!guardado) { sincronizar(); return; }
    limparTempoAtivoLS();   // o próximo pagehide regrava; parada explícita não religa
    const recente = Date.now() - guardado.ts < RETOMADA_MS;
    if (!recente || !localStorage.getItem('access')) { sincronizar(); return; }
    Promise.all(guardado.itens.map((item) =>
      axios.post(`${API_BASE}/api/bpo/tempo-cliente/`,
        { area: item.area, empresa: item.empresa, acao: 'iniciar' }, { headers: headers() })
        .catch(() => {})))
      .then(sincronizar);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // fechar aba / F5 / sair do app: para o relógio do cliente (fetch keepalive,
  // o cleanup do React não roda nesses caminhos) e registra para a retomada.
  // Cronômetro de tarefa NÃO é parado — ele continua correndo no servidor.
  useEffect(() => {
    const pararAoFechar = () => {
      const clientes = relogiosRef.current.filter((r) => r.tipo === 'cliente');
      if (!clientes.length) return;
      guardarTempoAtivoLS(clientes.map(({ area, empresa }) => ({ area, empresa })));
      try {
        const token = localStorage.getItem('access');
        clientes.forEach((r) => fetch(`${API_BASE}/api/bpo/tempo-cliente/`, {
          method: 'POST',
          keepalive: true,
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({ area: r.area, acao: 'parar' }),
        }));
      } catch { /* melhor esforço — o teto de 4h do servidor cobre o resto */ }
    };
    window.addEventListener('pagehide', pararAoFechar);
    return () => window.removeEventListener('pagehide', pararAoFechar);
  }, []);

  // mantém fresco: a cada 60s e a cada troca de página (GET leve)
  useEffect(() => {
    const t = setInterval(sincronizar, 60000);
    return () => clearInterval(t);
  }, [sincronizar]);
  useEffect(() => { sincronizar(); }, [location.pathname, sincronizar]);

  return (
    <TempoAtivoContext.Provider value={{ relogios, buscadoEm, sincronizar, pararRelogio }}>
      {children}
    </TempoAtivoContext.Provider>
  );
};

export const useTempoAtivo = () => useContext(TempoAtivoContext);
